import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataProvider, useNotify, useRedirect } from 'react-admin';
import { SubmitHandler, useForm } from 'react-hook-form';
import { getAuth, signOut } from 'firebase/auth';
import { IrmDataProvider } from '../../providers/types';

const MIN_LENGTH = 12;

type FormData = {
    newPassword: string;
    confirmPassword: string;
};

export const ChangeTempPasswordPage = () => {
    const dp = useDataProvider<IrmDataProvider>();
    const notify = useNotify();
    const redirect = useRedirect();
    const navigate = useNavigate();
    const [serverError, setServerError] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isValid },
    } = useForm<FormData>({
        mode: 'onChange',
        defaultValues: { newPassword: '', confirmPassword: '' },
    });

    const newPassword = watch('newPassword');

    const { mutate, isPending } = useMutation({
        mutationFn: (pw: string) => dp.acceptInvite(pw),
        onSuccess: () => {
            notify('Password set. Welcome.');
            // Hard-nav so checkAuth re-reads the now-approved user;
            // SPA redirect reuses the cached 'invited' identity.
            window.location.href = '/';
        },
        onError: async (e: any) => {
            // Firebase HttpsError surfaces the slug on e.details (string
            // or {code}); fall back to message for older clients.
            const slug: string =
                (typeof e?.details === 'string' && e.details) ||
                e?.details?.code ||
                e?.message ||
                '';

            switch (slug) {
                case 'invite_expired':
                case 'not_invited':
                case 'no_user':
                    setServerError('Your invite is no longer valid. Contact your administrator.');
                    await signOut(getAuth());
                    redirect('/login');
                    return;
                case 'password_required':
                case 'password_length':
                    setServerError('Password must be at least 12 characters.');
                    return;
                default:
                    // Auth password may have rotated even though the tx
                    // failed; re-submitting the same password succeeds.
                    setServerError(
                        'Activation didn’t complete. Submit your new password again.'
                    );
            }
        },
    });

    const onSubmit: SubmitHandler<FormData> = data => {
        setServerError(null);
        mutate(data.newPassword);
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Set your password
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    You signed in with a temporary password. Choose a new
                    password to activate your account.
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack spacing={2}>
                        <TextField
                            label="New password"
                            type="password"
                            autoComplete="new-password"
                            fullWidth
                            required
                            error={!!errors.newPassword}
                            helperText={
                                errors.newPassword
                                    ? `At least ${MIN_LENGTH} characters`
                                    : ' '
                            }
                            {...register('newPassword', {
                                required: true,
                                minLength: MIN_LENGTH,
                            })}
                        />
                        <TextField
                            label="Confirm password"
                            type="password"
                            autoComplete="new-password"
                            fullWidth
                            required
                            error={!!errors.confirmPassword}
                            helperText={
                                errors.confirmPassword
                                    ? 'Passwords do not match'
                                    : ' '
                            }
                            {...register('confirmPassword', {
                                required: true,
                                validate: v =>
                                    v === newPassword || 'mismatch',
                            })}
                        />
                        {serverError && (
                            <Alert severity="error">{serverError}</Alert>
                        )}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={!isValid || isPending}
                                startIcon={
                                    isPending ? (
                                        <CircularProgress size={16} />
                                    ) : undefined
                                }
                            >
                                Set password
                            </Button>
                            <Button
                                color="inherit"
                                onClick={async () => {
                                    await signOut(getAuth());
                                    navigate('/login');
                                }}
                            >
                                Sign out
                            </Button>
                        </Box>
                    </Stack>
                </form>
            </Paper>
        </Container>
    );
};

ChangeTempPasswordPage.path = '/change-temp-password';
